import { useMemo, useState } from 'react';
import { Topic } from '../../types/metadata';
import {
  ALL_INTEREST_TOPICS,
  POPULAR_INTEREST_TOPICS,
  interestTopicLabel,
} from '../../types/userProfile';
import Input from './Input';
import { chipToggleClassName } from './SelectableChips';

type TopicInterestPickerProps = {
  value: Topic[];
  onChange: (value: Topic[]) => void;
};

function toggleTopic(selected: Topic[], topic: Topic): Topic[] {
  return selected.includes(topic)
    ? selected.filter((item) => item !== topic)
    : [...selected, topic];
}

export default function TopicInterestPicker({ value, onChange }: TopicInterestPickerProps) {
  const [query, setQuery] = useState('');
  const [showMore, setShowMore] = useState(false);
  const needle = query.trim().toLowerCase();

  const visibleTopics = useMemo(() => {
    if (needle) {
      return ALL_INTEREST_TOPICS.filter((topic) =>
        interestTopicLabel(topic).toLowerCase().includes(needle)
      );
    }
    if (showMore) {
      return ALL_INTEREST_TOPICS;
    }
    const popular = new Set<Topic>(POPULAR_INTEREST_TOPICS);
    const extraSelected = value.filter((topic) => !popular.has(topic));
    return [...POPULAR_INTEREST_TOPICS, ...extraSelected];
  }, [needle, showMore, value]);

  return (
    <fieldset>
      <legend className="block text-sm font-medium text-gray-700 mb-2">Topics</legend>
      <p className="text-sm text-gray-500 mb-3">
        Start with popular topics, filter the list, or show more.
      </p>
      <Input
        label="Filter topics"
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search topics"
        autoComplete="off"
      />
      {!needle && (
        <p className="text-sm font-medium text-gray-700 mt-4 mb-2">
          {showMore ? 'All topics' : 'Popular topics'}
        </p>
      )}
      <div className={`flex flex-wrap gap-2 ${needle ? 'mt-4' : ''}`}>
        {visibleTopics.length === 0 ? (
          <p className="text-sm text-gray-500">No topics match that filter.</p>
        ) : (
          visibleTopics.map((topic) => {
            const selected = value.includes(topic);
            return (
              <button
                key={topic}
                type="button"
                aria-pressed={selected}
                onClick={() => onChange(toggleTopic(value, topic))}
                className={chipToggleClassName(selected)}
              >
                {interestTopicLabel(topic)}
              </button>
            );
          })
        )}
        {!needle && (
          <button
            type="button"
            aria-pressed={showMore}
            onClick={() => setShowMore((current) => !current)}
            className={chipToggleClassName(showMore, 'border border-primary/30')}
          >
            {showMore ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    </fieldset>
  );
}
